import { RefObject } from 'react';
import _ from 'lodash';

import { DATE_AND_TIME_FORMAT_LONG, DATE_FORMAT_SHORT } from '../AppConstants';
import type { Flag, FlagKey } from '../types/Flag';
import { dayjs } from './DateTime';
import { isNonNullable } from './TypingHelpers';

export const removeUnderscoreToCapitalize = (
    value: string | undefined,
    capitalizeAllWords: boolean = false
): string => {
    if (!value) return '';

    // Replace underscores and dashes with spaces, remove extra spaces, and trim
    const cleaned = value.replace(/[_-]/g, ' ').replace(/\s\s+/g, ' ').trim();

    if (capitalizeAllWords) {
        return cleaned
            .split(' ')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    }

    return cleaned.charAt(0).toUpperCase() + cleaned.slice(1).toLowerCase();
};

export const removeUnderscoreToUppercase = (value: string | undefined): string => {
    if (value) {
        // We remove single and multiple underscores
        const returnStr = value.replace(/[_-]/g, ' ').replace(/\s\s+/g, ' ').trim();
        return returnStr.toUpperCase();
    }
    return '';
};

export const formatTimestamp = (timestamp: number | undefined, format?: string, timezone?: string): string => {
    if (timestamp) {
        const _timezone = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
        const _format = format || DATE_AND_TIME_FORMAT_LONG;
        return dayjs.unix(timestamp).tz(_timezone).format(_format);
    }
    return 'Timestamp error';
};

export const formatDateTime = (dateVal: string | undefined, format = DATE_FORMAT_SHORT): string => {
    if (dateVal) {
        const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        return dayjs(dateVal).tz(userTimeZone).format(format);
    }
    return 'Date time error';
};

export const generateUrlWithParameters = (
    baseUrl: string,
    parameters: { client_id: string; response_type: string; redirect_uri: string }
): any => {
    const myUrlWithParams = new URL(baseUrl);
    Object.entries(parameters).forEach(([key, value]) => myUrlWithParams.searchParams.append(key, value));
    return myUrlWithParams;
};

export const clearAndFocusInputRef = (ref: RefObject<HTMLInputElement | null>): void => {
    if (ref?.current) {
        ref.current.value = '';
        ref.current.focus();
    }
};

const replaceDashWithSpace = (value: string): string => value.replace(/-/g, ' ');

const capitalize = (value: string): string => value.charAt(0).toLocaleUpperCase() + value.slice(1).toLocaleLowerCase();

export const formatFlagKey = (flagKey: FlagKey): string => {
    const [categoryName, subCategoryName] = flagKey.split('.').map(replaceDashWithSpace).map(capitalize);

    return `${categoryName}: ${subCategoryName}`;
};

export const formatFlag = (flag: Flag): string => {
    // The flag message is an optional field, so we need to check if it exists
    // If it exists, it should contain the formatted key for the flag
    if (flag.message) return flag.message;

    return formatFlagKey(flag.key);
};

// Adds a thousand separators to numbers
export const formatNumberThousandsSeparator = (numberVal: number | undefined) => {
    return isNonNullable(numberVal) ? numberVal.toLocaleString('en-US') : 'N/A';
};

/**
 * Formats object keys into human-readable strings (e.g., "mostRecentScanId" → "Most Recent Scan Id").
 *
 * Primarily used to convert column headers for file exports (CSV, Excel),
 * but can be reused for any case where readable labels are needed from object keys.
 *
 * Supports camelCase and snake_case formatting.
 */
export const formatObjectKeysToReadable = (data: Array<Record<string, unknown>>): Array<Record<string, unknown>> =>
    data.map((obj) => _.mapKeys(obj, (value: unknown, key: string) => _.startCase(key)));
